import * as React from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import IconMenu from 'components/IconMenu';
import { getFirstDayMonth, toISOFormat } from 'helpers/helpers';
import { DEFAULT_DATE_OPERATION } from 'helpers/filterHelper';
import { Highlighted } from 'ui/Highlighted/Highlighted';
import { translateBlock } from 'i18n';
import {
    TimeScales as TS,
    FilterOperations as FO,
    DynamicFilterOperations as DFO,
    SlidingTimeScales as STS,
} from 'business/definitions';
import {
    DEFAULT_DYNAMIC_VALUE,
    DEFAULT_TIME_SCALE,
    EXTRACT_OPERATION,
    TODAY,
} from 'business/constants';
import { isDynamicOperation, isTodayOperation } from './helpers';
import './_dateOperationSelector.scss';

const AVAILABLE_OPERATIONS = [
    DFO.current,
    DFO.last,
    DFO.next_,
    FO.greater_or_equal,
    FO.lower_or_equal,
    FO.between,
    FO.is_null,
    FO.is_not_null,
    EXTRACT_OPERATION,
] as const;

type Props = {
    value: string;
    field: string;
    operation: string;
    valueScale: TimeScalesT | null | undefined;
    // accepted arguments are SentenceFilterT, so it can be expanded if needed in the file
    setFilter: (arg1: {
        operation: FilterOperationsT;
        valueScale?: TimeScalesT | null;
        value?: string | null;
    }) => void;
    isPerimeterFilter: boolean;
    isDisabled: boolean;
    className: string;
};

function dateToString(date: Date | string) {
    const dateString =
        typeof date === 'string' ? date : date.toISOString().substr(0, 10);
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
}

const today = new Date();
const firstDayMonth = getFirstDayMonth(new Date());

function getDisplayableValueScale(
    valueScale?: TimeScalesT | null,
): TimeScalesT {
    if (
        valueScale &&
        _.values(TS)
            .filter((scale) => scale !== TS.dow)
            .includes(valueScale)
    )
        return valueScale;
    return DEFAULT_TIME_SCALE;
}

function DateOperationSelector({
    operation,
    value,
    field,
    isPerimeterFilter,
    valueScale,
    isDisabled = false,
    setFilter,
    className,
}: Props) {
    const { t } = useTranslation('sentence');

    // @ts-expect-error [TS migration] Was not detected by flow
    const handleChange = (menuValue) => {
        if (menuValue) {
            const [newOperation, newValueScale, newValue] =
                menuValue.split('-');

            if (newOperation === EXTRACT_OPERATION)
                setFilter({ operation: newOperation });
            else {
                let defaultValue = toISOFormat(today);
                if (newOperation === 'between')
                    defaultValue = `${toISOFormat(
                        firstDayMonth,
                    )}|${defaultValue}`;
                setFilter({
                    operation: newOperation,
                    valueScale: newValueScale || null,
                    value: newValue || defaultValue,
                });
            }
        }
    };

    // @ts-expect-error [TS migration] Was not detected by flow
    const handleSubMenuChange = (e, menuValue) => {
        // This is a hack to lower menu when we select the upper one
        // When we click on an embedded MenuItem, it cascades the click (capture) to the lower one,
        handleChange(menuValue);
        e.stopPropagation();
    };

    const getOptionsMenu = () => {
        const displayableValueScale = getDisplayableValueScale(valueScale);
        // @ts-expect-error [TS migration] Was not detected by flow
        const isDynamic = isDynamicOperation(operation);
        // @ts-expect-error [TS migration] Was not detected by flow
        const isTodayOp = isTodayOperation(operation, value);
        const isExtract = operation === EXTRACT_OPERATION;

        let startDate = dateToString(firstDayMonth);
        let endDate = dateToString(today);
        let dateValue = dateToString(today);
        let dynamicValue = DEFAULT_DYNAMIC_VALUE;

        if (operation === 'between' && value) {
            const [start, end] = value.split('|');
            if (start) startDate = dateToString(start);
            if (end) endDate = dateToString(end);
        } else if (isDynamic && value && value !== '1') dynamicValue = value;
        else if (!isDynamic && value && !isExtract && !isTodayOp)
            dateValue = dateToString(value);

        // If the filter is dynamic we take the values that are in its
        // context for translations
        const context = [TS.year, TS.week].includes(displayableValueScale)
            ? 'fem'
            : 'masc';

        // The dow time scale is available only with the extract operation
        const availableTimeScales = _.values({ ...TS, ...STS }).filter(
            (scale) => scale !== TS.dow,
        );
        return [
            [FO.greater_or_equal, FO.lower_or_equal].map((menuOperation) => (
                <MenuItem
                    value={menuOperation}
                    data-e2e-value={menuOperation}
                    key={menuOperation}
                >
                    {t(`dateOperations.${menuOperation}`)}{' '}
                    <Highlighted>{dateValue}</Highlighted>
                    <IconMenu
                        small
                        className="time-filter-menu"
                        iconElement={<ChevronRightIcon />}
                    >
                        <MenuItem
                            value={menuOperation}
                            data-e2e-value={menuOperation}
                            onClick={(e) => {
                                handleSubMenuChange(e, menuOperation);
                            }}
                        >
                            <Highlighted>{dateValue}</Highlighted>
                        </MenuItem>
                        <MenuItem
                            value={menuOperation}
                            data-e2e-value={menuOperation}
                            onClick={(e) => {
                                handleSubMenuChange(
                                    e,
                                    `${menuOperation}--${TODAY}`,
                                );
                            }}
                        >
                            <Highlighted>
                                {t('dateOperations.today')}
                            </Highlighted>
                        </MenuItem>
                    </IconMenu>
                </MenuItem>
            )),
            <MenuItem key="between" value="between" data-e2e-value="between">
                {t('between')} <Highlighted>{startDate}</Highlighted> {t('and')}{' '}
                <Highlighted>{endDate}</Highlighted>
            </MenuItem>,
            <Divider key="divider-1" />,
            <MenuItem
                key={`last-${displayableValueScale}`}
                value={`last-${displayableValueScale}-1`}
                data-e2e-value={`last_${displayableValueScale}`}
            >
                {t(`last_${displayableValueScale}`)}
                <IconMenu
                    small
                    className="time-filter-menu"
                    iconElement={<ChevronRightIcon />}
                >
                    {availableTimeScales.map((scale: string) => (
                        <MenuItem
                            key={scale}
                            value={`last-${scale}-1`}
                            onClick={(e) => {
                                handleSubMenuChange(e, `last-${scale}-1`);
                            }}
                        >
                            {t(`last_${scale}`)}
                        </MenuItem>
                    ))}
                </IconMenu>
            </MenuItem>,
            <MenuItem
                key={`current-${displayableValueScale}`}
                value={`current-${displayableValueScale}-1`}
                data-e2e-value={`current-${displayableValueScale}`}
            >
                {t(`current_${displayableValueScale}`)}
                <IconMenu
                    small
                    className="time-filter-menu"
                    iconElement={<ChevronRightIcon />}
                >
                    {Object.values(TS)
                        .filter((scale) => scale !== TS.dow)
                        .map((scale: string) => (
                            <MenuItem
                                key={scale}
                                value={`current-${scale}-1`}
                                onClick={(e) => {
                                    handleSubMenuChange(
                                        e,
                                        `current-${scale}-1`,
                                    );
                                }}
                            >
                                {t(`current_${scale}`)}
                            </MenuItem>
                        ))}
                </IconMenu>
            </MenuItem>,
            <Divider key="divider_2" />,
            <MenuItem
                value={`last-${displayableValueScale}-${dynamicValue}`}
                data-e2e-value="last"
                key={`last-${displayableValueScale}-${dynamicValue}`}
            >
                <Highlighted>{dynamicValue}</Highlighted>{' '}
                {t('last', { context })}{' '}
                <Highlighted>{t(displayableValueScale)}</Highlighted>
            </MenuItem>,
            <MenuItem
                value={`next_-${displayableValueScale}-${dynamicValue}`}
                data-e2e-value="next_"
                key={`next_-${displayableValueScale}-${dynamicValue}`}
            >
                <Highlighted>{dynamicValue}</Highlighted>{' '}
                {t('next_', { context })}{' '}
                <Highlighted>{t(displayableValueScale)}</Highlighted>
            </MenuItem>,
            <Divider key="divider_3" />,
            <MenuItem key="extract" value="extract" data-e2e-value="extract">
                {t('dateOperations.extract')}
            </MenuItem>,
            ...(!isPerimeterFilter // Perimeter filters can not be null
                ? [
                      <MenuItem
                          key="is_null"
                          value="is_null"
                          data-e2e-value="is_null"
                      >
                          {t('is_null')}
                      </MenuItem>,
                      <MenuItem
                          key="is_not_null"
                          value="is_not_null"
                          data-e2e-value="is_not_null"
                      >
                          {t('is_not_null')}
                      </MenuItem>,
                  ]
                : []),
        ];
    };

    // @ts-expect-error [TS migration] Was not detected by flow
    const translateOperation = (menuValue) => {
        const newOperation = menuValue.split('-')[0];

        const isDefaultOperation =
            newOperation === DEFAULT_DATE_OPERATION && !value;

        const newValueScale = valueScale || DEFAULT_TIME_SCALE;
        const isDynamic = isDynamicOperation(newOperation);
        const isTodayOp = isTodayOperation(newOperation, value);
        const isDateOperation = AVAILABLE_OPERATIONS.includes(newOperation);
        if (
            (isPerimeterFilter && !value && !isDateOperation) ||
            isDefaultOperation
        )
            return translateBlock(field);
        if (isDynamic && newValueScale && value === '1')
            return t(`${newOperation}_${newValueScale}`);
        if (!newOperation) return t('noTimeFilter');
        if (isTodayOp)
            return newOperation === FO.greater_or_equal
                ? t('dateOperations.after_today')
                : t('dateOperations.before_today');
        if (isDateOperation) return t(`dateOperations.${newOperation}`);
        return t(newOperation);
    };

    // We map all MenuItems with a specific action
    let menuValue = operation;
    if (['current', 'last'].includes(operation) && valueScale)
        menuValue += `-${valueScale}-${value}`;
    if (!AVAILABLE_OPERATIONS.includes(operation)) {
        let newOperation: Extract<
            FilterOperationsT,
            'greater_or_equal' | 'lower_or_equal'
        > = DEFAULT_DATE_OPERATION;

        // FIXME: there are still some charts that have time filters with "greater" or "lower"
        // since "greater" and "lower" are not available anymore in the frontend, we need to match them
        // with the closest operation
        if (operation === FO.greater) {
            newOperation = FO.greater_or_equal;
        }
        if (operation === FO.lower) {
            newOperation = FO.lower_or_equal;
        }

        menuValue = newOperation;
        setFilter({ operation: newOperation });
    }
    return (
        <FormControl variant="standard" fullWidth={false} className={className}>
            <Select
                variant="standard"
                key={`${operation}-${valueScale || 'null'}-${value}`}
                disabled={isDisabled}
                renderValue={translateOperation}
                value={menuValue}
                onChange={(e) => {
                    handleChange(e.target.value);
                }}
                IconComponent={KeyboardArrowDownIcon}
                disableUnderline
            >
                {getOptionsMenu()}
            </Select>
        </FormControl>
    );
}
const areItemEqual = (prevProps: Props, nextProps: Props) =>
    nextProps.operation === prevProps.operation &&
    nextProps.value === prevProps.value &&
    nextProps.valueScale === prevProps.valueScale;

export default React.memo<Props>(DateOperationSelector, areItemEqual);
